import { FontIcon, getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPDefaultButton,
  IPIconButton,
  IPModal,
  IPPivot,
  IPPivotItem,
  IPPrimaryButton,
  IPSeparator,
  IPText,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ActivityTrail from 'components/Activity/ActivityTrail';
import AuditTrail from 'components/Audit/AuditTrail';
import { ChecklistContainer, ChecklistMode } from 'components/Checklist/ChecklistContainer';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import TemplateEvidenceLocationModal from 'components/Tasks/Evidence/TemplateEvidenceLocationModal';
import TaskLinkList from 'components/Links/LinkLists/TaskLinkList';
import OverlayLoader from 'components/Loading/OverlayLoader';
import { ErrorMessage } from 'components/Notification/ErrorMessage';
import { cancelIcon, globalStackStylesHeight100, globalStackTokensGapSmall, templateIcon } from 'globalStyles';
import Joi from 'joi';
import Task from 'models/tasks/task';
import { TaskCheckList } from 'models/tasks/taskHelperClasses';
import TaskType from 'models/tasks/taskType';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskContext } from '../Context/TaskContext';
import TaskTypeForm from '../Forms/TaskTypeForm';
import { ISingleTaskTypeProps } from '../SingleTask';
import { canUpdateTaskField, TaskFieldTypes } from '../TaskAuthHelper';
import TaskTopTasksBar from '../TaskTopTasksBar';
import TaskWorkflows from '../TaskWorkflows';
import { getTaskViewModalStackStyles, getTaskViewModalStyles } from '../View/TaskRenderHelpers';
import { TaskDetailOwner } from './Details/TaskDetailOwner';
import { TemplateTaskDetail } from './TemplateTaskDetail';
import { useTaskPivotCounts } from './useTaskPivotCounts';

interface ITemplateTaskProps extends ISingleTaskTypeProps {
  onSaveAsNewTask: () => void;
  onChangeCompletionDate: (completionDate: Date) => void;
}

const TemplateTask = (props: ITemplateTaskProps) => {
  const { t } = useTranslation(['task', 'control', 'translation', 'dateTimeComponent']);
  const appContext = useContext(AppContext);
  const [err, setErr] = useState<Joi.ValidationError | undefined>(undefined);
  const [selectedPivot, setSelectedPivot] = useState<string>(props.selectedPivot ?? 'Checklist');
  const [showRemoveDialog, setShowRemoveDialog] = useState<boolean>(false);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const { pivotCounts, onActivityCountChange } = useTaskPivotCounts(props.task);
  const [showEvidenceLocation, setShowEvidenceLocation] = useState<boolean>(false);
  const theme = getTheme();

  useEffect(() => {
    const _localizedFields = {
      assignment: t('task:Assignment.Label'),
      startDateTime: t('task:Start.Label'),
      endDateTime: t('task:End.ErrorMessage'),
      name: t('task:Name.Label'),
      deadline: t('task:Deadline.ErrorMessage'),
    };

    const result = props.task.validate(_localizedFields);
    setErr(result.error);
  }, [t, props.task]);

  useEffect(() => {
    if (appContext.isMobileView) {
      setSelectedPivot('TaskDetails');
    } else if (props.task.taskTypeId && props.hasFormFeature) {
      setSelectedPivot('TaskType');
    } else {
      setSelectedPivot('Checklist');
    }
  }, [appContext.isMobileView, appContext.windowSize, props.hasFormFeature, props.task.taskTypeId]);

  useEffect(() => {
    //Single task requests to show a specific pivot
    if (props.selectedPivot) {
      setSelectedPivot(props.selectedPivot);
    }
  }, [props.selectedPivot]);

  const validate = (fieldName: string): string | undefined => {
    if (err) {
      const fieldError = err.details.find((e) => {
        return e.context?.key === fieldName || e.message.toLowerCase().includes(fieldName.toLowerCase());
      });

      return fieldError ? fieldError.message : undefined;
    }

    return undefined;
  };

  const hasErrors = (): boolean => {
    return err ? true : false;
  };

  const cannotSaveTask = (): boolean => {
    return (props.task.taskId !== -1 && props.task.isEqual(props.orgTask)) || hasErrors();
  };

  const footer = (
    <IPStack horizontal={!appContext.isMobileView} verticalAlign="end" tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPStack horizontal tokens={globalStackTokensGapSmall}>
          <IPPrimaryButton
            disabled={cannotSaveTask() || props.isActionPending}
            onClick={async () => {
              props.onSave(props.task, false);
            }}
          >
            {t('translation:General.Button.Save')}
          </IPPrimaryButton>
          <IPDefaultButton
            onClick={() => {
              onCancel();
            }}
          >
            {t('translation:General.Button.Cancel')}
          </IPDefaultButton>
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );

  const onCancel = () => {
    if (cannotSaveTask()) {
      props.onCancel();
    } else {
      setShowCancelDialog(true);
    }
  };

  const onUpdateCheckList = (newCheckList: TaskCheckList) => {
    const copyTask = props.task.clone();
    copyTask.checkList = newCheckList;
    props.onUpdateTask(copyTask);
  };

  const getTemplateTasks = () => {
    return (
      <TemplateTaskDetail
        task={props.task}
        orgTask={props.orgTask}
        onUpdate={(newTask: Task) => {
          props.onUpdateTask(newTask);
        }}
        getErrorMessage={validate}
        isActionPending={props.isActionPending}
        tags={props.tags}
        addTagToTaskState={props.addTagToTaskState}
        removeTagFromTaskState={props.removeTagFromTaskState}
        onChangeCompletionDate={props.onChangeCompletionDate}
        canUpdate={props.canUpdate}
      />
    );
  };

  const onClickForm = (taskTypeId: number) => {
    const newTask = props.task.clone();
    newTask.assignForm(taskTypeId > 0 ? appContext.globalDataCache.taskTypes.get(taskTypeId) : undefined);

    props.onUpdateTask(newTask);
    if (taskTypeId > 0) {
      setSelectedPivot('TaskType');
    } else {
      setSelectedPivot('Checklist');
    }
  };

  //
  // Main render
  //
  return (
    <IPModal forceFocusInsideTrap={false} isOpen={props.isOpen} styles={getTaskViewModalStyles(props.windowLevel)}>
      {props.isLoading && <OverlayLoader text={t('task:Progress.Save')} />}
      <IPStack
        verticalFill
        tokens={globalStackTokensGapSmall}
        styles={getTaskViewModalStackStyles(appContext.isMobileView, false)}
      >
        <IPStack.Item>
          <IPStack horizontal verticalAlign="center" horizontalAlign="space-between">
            <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
              {!appContext.isMobileView && (
                <IPStack.Item>
                  <FontIcon
                    {...templateIcon}
                    style={{
                      fontSize: 28,
                      color: appContext.useDarkMode ? theme.palette.themeLight : theme.palette.themeSecondary,
                    }}
                  />
                </IPStack.Item>
              )}
              <IPStack.Item>
                <IPText variant="xxLarge">
                  {props.task.taskId === -1 && props.task.isSeries() && t('task:Title.Template.Series.New')}
                  {props.task.taskId === -1 && !props.task.isSeries() && t('task:Title.Template.New')}
                  {props.task.taskId !== -1 && props.task.isSeries() && t('task:Title.Template.Series.Edit')}
                  {props.task.taskId !== -1 && !props.task.isSeries() && t('task:Title.Template.Edit')}
                </IPText>
              </IPStack.Item>
            </IPStack>
            {!appContext.isMobileView && (
              <IPStack.Item>
                <TaskTopTasksBar
                  task={props.task}
                  onClickForm={onClickForm}
                  onClickRemove={() => setShowRemoveDialog(true)}
                  onCreateNewTaskTemplate={props.onCreateNewTaskTemplate}
                  onSaveAsNewTask={props.onSaveAsNewTask}
                  navigateExternal={props.navigateExternal}
                  canUpdate={props.canUpdate}
                  canDelete={props.canDelete}
                  orgTask={props.orgTask}
                  onConvertChecklist={props.onConvertChecklist}
                  canSave={!hasErrors()}
                  isLoading={props.isLoading}
                  isActionPending={props.isActionPending}
                  onEvidenceLocation={() => setShowEvidenceLocation(true)}
                />
              </IPStack.Item>
            )}
            <IPStack.Item>
              <IPIconButton iconProps={cancelIcon} onClick={onCancel} />
            </IPStack.Item>
          </IPStack>
          <IPStack.Item>
            <TaskDetailOwner
              orgTask={props.orgTask}
              task={props.task}
              onUpdate={props.onUpdateTask}
              canUpdate={props.canUpdate}
            />
          </IPStack.Item>
          <IPSeparator />
          <ErrorMessage error={props.error} customAction={props.onAfterErrorAction} />
        </IPStack.Item>
        {appContext.isMobileView && (
          <IPStack.Item>
            <TaskTopTasksBar
              task={props.task}
              onClickForm={onClickForm}
              onClickRemove={() => setShowRemoveDialog(true)}
              onCreateNewTaskTemplate={props.onCreateNewTaskTemplate}
              onSaveAsNewTask={props.onSaveAsNewTask}
              navigateExternal={props.navigateExternal}
              canUpdate={props.canUpdate}
              canDelete={props.canDelete}
              orgTask={props.orgTask}
              onConvertChecklist={props.onConvertChecklist}
              canSave={!hasErrors()}
              isLoading={props.isLoading}
              isActionPending={props.isActionPending}
              onEvidenceLocation={() => setShowEvidenceLocation(true)}
            />
          </IPStack.Item>
        )}
        <IPStack.Item grow>
          <IPStack horizontal styles={globalStackStylesHeight100} tokens={globalStackTokensGapSmall}>
            {!appContext.isMobileView && (
              <Fragment>
                <IPStack.Item styles={{ root: { width: '50%' } }}>{getTemplateTasks()}</IPStack.Item>
                <IPSeparator vertical></IPSeparator>
              </Fragment>
            )}
            <IPStack.Item styles={!appContext.isMobileView ? { root: { width: '50%' } } : { root: { width: '100%' } }}>
              <IPStack verticalFill tokens={globalStackTokensGapSmall}>
                <IPStack.Item>
                  <IPPivot
                    selectedKey={selectedPivot}
                    onLinkClick={(item) => setSelectedPivot(item?.props.itemKey ?? '')}
                    overflowBehavior="menu"
                  >
                    {appContext.isMobileView && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-details"
                        key="TaskDetails"
                        itemKey="TaskDetails"
                        headerText={t('task:Pivot.TaskDetails')}
                      />
                    )}
                    {props.hasFormFeature && props.task.taskTypeId && props.task.taskTypeId > 0 && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-tasktype"
                        key="TaskType"
                        itemKey="TaskType"
                        headerText={TaskType.tabNameForTask(props.task.taskTypeId, props.task.formVersion, appContext)}
                      />
                    )}
                    {(!props.task.taskTypeId || props.task.taskTypeId === 0 || !props.hasFormFeature) && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-checklist"
                        key="Checklist"
                        itemKey="Checklist"
                        headerText={t('task:Pivot.Checklist')}
                        itemCount={pivotCounts.checklist}
                      />
                    )}
                    <IPPivotItem
                      className="isoplanner-task-pivot-context"
                      key="Context"
                      itemKey="Context"
                      headerText={t('task:Pivot.Context')}
                      itemCount={pivotCounts.context}
                    />
                    <IPPivotItem
                      className="isoplanner-task-pivot-library"
                      key="Resources"
                      itemKey="Resources"
                      headerText={t('task:Pivot.Resources')}
                      itemCount={pivotCounts.resources}
                    />
                    {props.hasPAFeature && props.task.isSeries() && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-workflows"
                        key="Workflows"
                        itemKey="Workflows"
                        headerText={t('task:Pivot.Workflows')}
                      />
                    )}
                    {props.task.commentTrailId > 0 && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-activity"
                        key="Activities"
                        itemKey="Activities"
                        headerText={t('task:Pivot.Activities')}
                        itemCount={pivotCounts.activity}
                      />
                    )}
                    {props.task.auditTrailId > 0 && (
                      <IPPivotItem
                        className="isoplanner-task-pivot-changelog"
                        key="Audit"
                        itemKey="Audit"
                        headerText={t('task:Pivot.Audit')}
                      />
                    )}
                  </IPPivot>
                </IPStack.Item>
                {selectedPivot === 'TaskDetails' && (
                  <Fragment>
                    <IPStack.Item grow style={{ height: appContext.isMobileView ? '40vh' : 0 }}>
                      {getTemplateTasks()}
                    </IPStack.Item>
                  </Fragment>
                )}
                <TaskTypeForm
                  isOpen={selectedPivot === 'TaskType'}
                  task={props.task}
                  links={props.links}
                  compact={true}
                  readonly={true}
                  allowEdit={true}
                  onUpdateData={props.onUpdateKPIData}
                  onSetData={props.onSetKPIData}
                  onUpdateTaskForForm={props.onUpdateTaskForForm}
                  addLinks={props.addLinkToTaskState}
                  setLinks={props.setLinkToTaskState}
                  removeLink={props.removeLinkFromTaskState}
                  showErrors={props.showFormErrors}
                  onValidate={props.onValidateForm}
                ></TaskTypeForm>
                {selectedPivot === 'Checklist' && (
                  <ChecklistContainer
                    task={props.task}
                    orgTask={props.orgTask}
                    onUpdateCheckList={onUpdateCheckList}
                    mode={ChecklistMode.Creator}
                    canUpdate={props.canUpdate}
                  />
                )}
                {selectedPivot === 'Workflows' && <TaskWorkflows task={props.task} />}
                {selectedPivot === 'Resources' && (
                  <TaskLinkList
                    taskLinks={props.links}
                    task={props.task}
                    addLinks={props.addLinkToTaskState}
                    setLinks={props.setLinkToTaskState}
                    removeLink={props.removeLinkFromTaskState}
                    navigateExternal={props.navigateExternal}
                  />
                )}
                {selectedPivot === 'Activities' && (
                  <IPStack.Item grow>
                    <ActivityTrail
                      auditTrailId={props.task.auditTrailId}
                      commentTrailId={props.task.commentTrailId}
                      taskId={props.task.taskId}
                      authSchemaIds={[props.task.authSchemaId]}
                      onCommentCountChange={onActivityCountChange}
                      onError={props.onError}
                    />
                  </IPStack.Item>
                )}
                {selectedPivot === 'Audit' && (
                  <IPStack.Item grow>
                    <AuditTrail auditTrailId={props.task.auditTrailId} />
                  </IPStack.Item>
                )}
                {selectedPivot === 'Context' && (
                  <IPStack.Item grow>
                    <TaskContext
                      orgTask={props.orgTask}
                      task={props.task}
                      updateTaskForForm={props.onUpdateTaskForForm}
                      updateTask={props.onUpdateTask}
                      allowEdit={canUpdateTaskField(
                        props.orgTask,
                        props.canUpdate,
                        TaskFieldTypes.ChecklistEdit,
                        appContext,
                      )}
                      windowLevel={props.windowLevel}
                      navigateExternal={true}
                    />
                  </IPStack.Item>
                )}
              </IPStack>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
        <IPStack.Item>
          <IPSeparator styles={{ root: { height: 1 } }} />
        </IPStack.Item>
        <IPStack.Item>{footer}</IPStack.Item>
      </IPStack>
      <DialogConfirmDelete
        onNo={() => {
          setShowRemoveDialog(false);
        }}
        onYes={() => {
          setShowRemoveDialog(false);
          props.onRemove();
        }}
        title={props.task.isSeries() ? t('task:Template.RemoveDialogTitle') : t('task:Remove.DialogTitle')}
        subText={props.task.isSeries() ? t('task:Template.RemoveDialogSubText') : t('task:Remove.DialogSubTitle')}
        hidden={!showRemoveDialog}
        confirmText={!props.task.isSeries() ? t('task:Remove.RemoveDialogConfirmText') : undefined}
      />
      <DialogYesNo
        onNo={() => {
          setShowCancelDialog(false);
        }}
        onYes={() => {
          setShowCancelDialog(false);

          props.onCancel();
        }}
        title={t('task:Cancel.DialogTitle')}
        subText={t('task:Cancel.DialogSubTitle')}
        hidden={!showCancelDialog}
      />
      {showEvidenceLocation && (
        <TemplateEvidenceLocationModal
          isOpen={showEvidenceLocation}
          task={props.task}
          onClose={() => setShowEvidenceLocation(false)}
        />
      )}
    </IPModal>
  );
};

export default TemplateTask;
